import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';

import { fetchBudgetById, fetchBudgetUserProfile, fetchBudgetAttachments, fetchBudgetHistory } from '../../../api/fetchBudget';
import {
  BudgetStatusEnum,
  BudgetStatusFilterMap,
  BudgetStatusLabelMap,
  getBudgetStatusLabel,
} from '../../../enums/budgetStatus';
import { BudgetRecipientEnum, IBudgetFormData, IFileItem } from '../createAndEdit/types';
import { getStatusColor } from '../utils';
import DetailActions from './components/DetailActions';
import DetailCounterProposal from './components/DetailCounterProposal';
import DetailFiles from './components/DetailFiles';
import DetailInfoBar from './components/DetailInfoBar';
import DetailServicesSection from './components/DetailServicesSection';
import DetailSummary from './components/DetailSummary';
import DetailTitle from './components/DetailTitle';
import DetailVehicleAndClientCards from './components/DetailVehicleAndClientCards';
import { useDetailJourneySelector } from './hooks/useDetailJourneySelector';
import { useTruckerPayPlan } from '../createAndEdit/hooks/useTruckerPayPlan';
import BudgetCoverageBalance from '../createAndEdit/components/BudgetCoverageBalance';

interface IDetailBudgetProps {
  occurrenceId: string;
  budgetId: string;
  onClose?: () => void;
  onAccept?: (counterProposalValue?: number, counterProposalObs?: string, subscriptionBalanceToApply?: number) => void;
  onReject?: (counterProposalValue?: number, counterProposalObs?: string) => void;
  initialData?: IBudgetFormData;
  budgetStatus?: BudgetStatusEnum | string;
  budgetDate?: string;
  onViewAttachments?: (occurrenceId: string) => void;
  redirectBack?: string;
}

const DetailBudget = (props: IDetailBudgetProps) => {
  const [budgetData, setBudgetData] = useState<IBudgetFormData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileSelected, setProfileSelected] = useState<BudgetRecipientEnum | string | undefined>(undefined);
  const [currentCounterProposalValue, setCurrentCounterProposalValue] = useState<number | undefined>(undefined);
  const [currentCounterProposalObs, setCurrentCounterProposalObs] = useState<string | undefined>(undefined);
  const [hasCounterProposal, setHasCounterProposal] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<IFileItem[]>([]);
  const [lastVersionNumber, setLastVersionNumber] = useState<number | undefined>(undefined);
  const [useCoverageBalance, setUseCoverageBalance] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const loadBudgetData = async () => {
      setLoading(true);
      try {
        if (props.initialData) {
          setBudgetData(props.initialData);
          setCurrentCounterProposalValue(props.initialData.counterProposalValue);
          setCurrentCounterProposalObs(props.initialData.counterProposalObs);
          setLastVersionNumber(props.initialData.versionNumber);
          setUseCoverageBalance(!!props.initialData.subscriptionBalanceToApply)
        } else {
          const data = await fetchBudgetById(props.budgetId, props.occurrenceId);
          if (data) {
            setBudgetData(data);
            setCurrentCounterProposalValue(data.counterProposalValue);
            setCurrentCounterProposalObs(data.counterProposalObs);
          } else {
            setBudgetData(null);
            props.onClose?.();
          }
        }
        const lastVersion = await fetchBudgetHistory(props.budgetId, 'versionNumber', 'desc', 1, 1, true);
        setLastVersionNumber(lastVersion.data[0].version);
      } catch (error) {
        console.error('[DetailBudget] Error loading budget data:', error);
        setBudgetData(null);
        props.onClose?.();
      } finally {
        setLoading(false);
      }
    };

    const loadUserRole = async () => {
      const userProfile = await fetchBudgetUserProfile();
      if (userProfile) {
        setProfileSelected(userProfile);
      }
    };

    loadBudgetData();
    loadUserRole();
  }, [props.budgetId, props.occurrenceId, props.initialData]);

  useEffect(() => {
    const loadAttachments = async () => {
      if (budgetData?.budgetId) {
        try {
          const budgetAttachments = await fetchBudgetAttachments(budgetData.budgetId);
          const mappedFiles: IFileItem[] = budgetAttachments.map((attachment) => ({
            name: attachment.name,
            type: attachment.contentType,
            size: attachment.contentSize,
            link: attachment.link,
            updatedAt: attachment.updatedAt,
          }));
          setAttachments(mappedFiles);
        } catch (error) {
          console.error('[DetailBudget] Error loading attachments:', error);
          setAttachments([]);
        }
      }
    };

    loadAttachments();
  }, [budgetData?.budgetId]);

  const vehicleData = useMemo(() => {
    return budgetData?.vehicleData;
  }, [budgetData]);

  const clientData = useMemo(() => {
    return budgetData?.clientData;
  }, [budgetData]);

  const services = useMemo(() => {
    return budgetData?.services || [];
  }, [budgetData]);

  const truckerPayPlan = useTruckerPayPlan(
    vehicleData?.chassis ?? '',
    vehicleData?.odometer ? parseInt(vehicleData.odometer.replace(/\D/g, "")) : 0,
    vehicleData?.hourmeter ? parseInt(vehicleData.hourmeter.replace(/\D/g, "")) : 0,
    services
  );

  const summary = useMemo(() => {
    if (!budgetData?.services) {
      const existingSummary = budgetData?.summary;
      return {
        subtotal: existingSummary?.subtotal || 0,
        discounts: existingSummary?.discounts || 0,
        subscriptionBalanceApplied: existingSummary?.subscriptionBalanceApplied || 0,
        totalToPay: existingSummary?.totalToPay || 0,
        counterProposalValue: budgetData?.counterProposalValue,
      };
    }

    let totalSubtotal = 0;
    let totalDiscounts = 0;

    for (const service of budgetData.services) {
      let serviceSubtotal = 0;
      
      for (const item of service.items) {
        if (item.removed) continue;
        if (item.isCovered === true) continue;

        const itemValue = item.value || 0;
        let clean = String(item.quantity || '0').replace(/[^\d.,]/g, '');
        clean = clean.replace(',', '.');
        const parsedQuantity = Number.parseFloat(clean);
        const quantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;

        if (quantity <= 0) continue;

        const discountStr = String(item.discount || '0%');
        let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, '');
        cleanDiscountStr = cleanDiscountStr.replaceAll(',', '.');
        const itemDiscountPercent = Number.parseFloat(cleanDiscountStr) || 0;

        const grossValue = itemValue * quantity;
        const itemDiscount = (grossValue * itemDiscountPercent) / 100;
        const itemSubtotal = grossValue - itemDiscount;
        
        serviceSubtotal += itemSubtotal;
      }

      const serviceDiscountPercent = 
        (service.volkstotalPercentage || 0) +
        (service.dealershipPercentage || 0) +
        (service.warrantyPercentage || 0);
      
      const serviceDiscount = (serviceSubtotal * serviceDiscountPercent) / 100;

      totalSubtotal += serviceSubtotal;
      totalDiscounts += serviceDiscount;
    }

    const balanceToApply = useCoverageBalance ? Math.min(truckerPayPlan?.data?.availableCoverageBalance ?? 0, totalSubtotal - totalDiscounts) : 0;

    const totalToPay = totalSubtotal - totalDiscounts - (budgetData?.subscriptionBalanceToApply || balanceToApply);

    return {
      subtotal: totalSubtotal,
      discounts: totalDiscounts,
      totalToPay: totalToPay,
      subscriptionBalanceApplied: budgetData?.subscriptionBalanceToApply || balanceToApply,
      counterProposalValue: budgetData?.counterProposalValue,
    };
  }, [budgetData, useCoverageBalance]);

  const counterProposalValue = useMemo(() => {
    return budgetData?.counterProposalValue;
  }, [budgetData]);

  const counterProposalObs = useMemo(() => {
    return budgetData?.counterProposalObs;
  }, [budgetData]);

  const formatDateTime = (iso?: string): string => {
    if (!iso) return '';
    let isoString = iso.trim();
    if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
      isoString = `${isoString}Z`;
    }
    const d = new Date(isoString);
    const date = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
    const time = d.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });
    return `${date} - ${time}`;
  };

  const currentStatus = budgetData?.status || props.budgetStatus;
  const budgetRecipient = budgetData?.recipient;

  const { activeJourney } = useDetailJourneySelector({
    userProfile: profileSelected,
    budgetRecipient,
    budgetStatus: currentStatus,
    isLastVersion: budgetData?.versionNumber === lastVersionNumber,
  });

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!budgetData) {
    return <div>Orçamento não encontrado</div>;
  }

  return (
    <div className="w-full">
      <DetailTitle
        occurrenceProtocolNumber={budgetData.vehicleData?.protocolNumber}
        occurrenceContractNumber={budgetData.vehicleData?.contractNumber}
        budgetNumber={budgetData.number}
        budgetId={budgetData.budgetId}
        onClose={props.onClose}
        shouldShowCloseIcon={activeJourney.shouldShowCloseIcon}
        onViewAttachments={props.onViewAttachments ? () => props.onViewAttachments?.(props.occurrenceId) : undefined}
        budgetData={budgetData}
        redirectBack={props.redirectBack}
      />
      <DetailVehicleAndClientCards vehicleData={vehicleData} clientData={clientData} />
      <DetailInfoBar 
        userProfile={budgetData.userProfile} 
        origin={budgetData.origin} 
        originUserName={budgetData.originUserName} 
        recipient={budgetData.recipient} 
      />

      {
        truckerPayPlan.showCoverageBalanceBox({
          isEditMode: true,
          isBudgetApproved: currentStatus === BudgetStatusLabelMap[BudgetStatusEnum.APPROVED],
          profile: profileSelected as BudgetRecipientEnum
        }) && (
        <BudgetCoverageBalance
          truckerPay={truckerPayPlan.data}
          useCoverageBalance={useCoverageBalance}
          hasCoverageBalance={truckerPayPlan.hasCoverageBalance}
          isMobile={isMobile}
          onUseCoverageBalanceChange={(e) => {
            setUseCoverageBalance(e);
            
          }}
        />
      )}

      <div className={`mb-6 ${isMobile ? 'px-4' : 'px-6'}`}>
        <div className={`font-semibold mb-4 ${isMobile ? 'text-size-16' : 'text-size-18'}`}>Valores</div>
        {services.map((service, index) => (
          <DetailServicesSection
            key={service.id || `service-${index}`}
            service={service}
            serviceIndex={index}
          />
        ))}
      </div>
      
      {summary && (
        <div className={`mb-6 ${isMobile ? 'px-4' : 'px-6'}`}>
          <DetailSummary summary={summary} counterProposalAcceptedAt={budgetData?.counterProposalAcceptedAt} />
        </div>
      )}

      {attachments.length > 0 && (
        <DetailFiles files={attachments} budgetId={budgetData.budgetId} />
      )}

      {activeJourney.shouldShowCounterProposal && (
        <DetailCounterProposal
          counterProposal={true}
          counterProposalValue={currentCounterProposalValue ?? counterProposalValue ?? 0}
          counterProposalObs={currentCounterProposalObs ?? counterProposalObs}
          onCounterProposalValueChange={(value) => {
            setCurrentCounterProposalValue(value);
            setHasCounterProposal(value !== undefined && value !== null && value > 0);
          }}
          onCounterProposalObsChange={(value) => setCurrentCounterProposalObs(value)}
        />
      )}

      {(() => {
        const status = budgetData.status || props.budgetStatus;
        // Normalize status: convert Portuguese string to enum if needed
        const normalizedStatus =
          status && status in BudgetStatusFilterMap
            ? BudgetStatusFilterMap[status as string]
            : (status as BudgetStatusEnum);
        const isRejectedOrApproved =
          normalizedStatus === BudgetStatusEnum.REJECTED ||
          normalizedStatus === BudgetStatusEnum.APPROVED ||
          status === 'Recusado' ||
          status === 'Aprovado';
        const cpValue = budgetData.counterProposalValue;
        const cpObs = budgetData.counterProposalObs;
        const hasCounterProposal =
          (cpValue !== undefined && cpValue !== null && cpValue > 0) ||
          (cpObs !== undefined && cpObs !== null && cpObs.trim() !== '');

        if (isRejectedOrApproved && hasCounterProposal) {
          return (
            <div className="mb-6">
              <DetailCounterProposal
                counterProposal={true}
                counterProposalValue={cpValue ?? 0}
                counterProposalObs={cpObs}
                readOnly={true}
              />
            </div>
          );
        }
        return null;
      })()}

      {(budgetData.status || props.budgetStatus) && (
        <div className={`mb-6 ${isMobile ? 'px-4' : 'px-6'}`}>
          <div className={`font-bold mb-4 flex ${isMobile ? 'flex-col gap-2' : 'flex-row items-center gap-2 justify-between'}`}>
            <span
              className={`min-w-60 px-3 py-2 rounded-full text-size-12 text-center inline-block whitespace-nowrap ${getStatusColor(
                (budgetData.status || props.budgetStatus) as string,
              )}`}
            >
              {getBudgetStatusLabel(budgetData.status || props.budgetStatus || '')}
            </span>
            <span>
              {(() => {
                const status = budgetData.status || props.budgetStatus;
                if (status === BudgetStatusEnum.APPROVED && budgetData.counterProposalAcceptedAt) {
                  return formatDateTime(budgetData.counterProposalAcceptedAt);
                }
                if (props.budgetDate) {
                  return formatDateTime(props.budgetDate);
                }
                return '-';
              })()}
            </span>
          </div>
        </div>
      )}

      {activeJourney.shouldShowActions && (
        <div className={`mb-6 ${isMobile ? 'px-4' : 'px-6'}`}>
          <DetailActions
            hasCounterProposal={hasCounterProposal}
            onAccept={() => 
              props.onAccept?.(
                currentCounterProposalValue, 
                currentCounterProposalObs, 
                useCoverageBalance ? 
                  Math.min(
                    truckerPayPlan.data?.availableCoverageBalance ?? 0,
                    summary.totalToPay
                  )
                  : undefined
              )
            }
            onReject={() =>
              props.onReject?.(currentCounterProposalValue, currentCounterProposalObs)
            }
          />
        </div>
      )}

      {activeJourney.shouldShowCloseButton && props.onClose && (
        <div className={`mb-6 ${isMobile ? 'px-4' : 'px-6'}`}>
          <div className={`flex ${isMobile ? 'justify-center' : 'justify-end'}`}>
            <button
              type="button"
              className={`${isMobile ? 'w-full' : 'px-6'} py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-size-15 font-bold`}
              onClick={props.onClose}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailBudget;
